import { FunctionComponent, RefObject, useState } from "react";
import { useTranslation } from "react-i18next";

import { faEuroSign, faPlay } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { useIsFeatureActive } from "@/utils/useIsFeatureActive";

import { useAppSelector } from "@/redux/hooks";
import { authSelector } from "@/pages/Public/redux/auth.slice";

import { PauseOutreachButton } from "@/pages/Private/pages/Contacts/components/LeadPanelActions/PauseOutreachButton";
import { useLeadPanelActions } from "@/pages/Private/pages/Contacts/components/LeadPanelActions/useLeadPanelActions";
import { resolveOutreachHoldState } from "@/pages/Private/pages/Contacts/components/LeadPanelActions/outreachHoldState";
import { useGetZohoOAuthStatusQuery } from "@/pages/Private/redux/zoho/zoho.api";
import { useGetPipedriveOAuthStatusQuery } from "@/pages/Private/redux/pipedrive/pipedrive.api";

import { FeaturesCodes } from "../../../Users/enum/codes";
import { ListPerson } from "../../../List/schema/list";
import { CreateHubspotDealModal } from "../../../Company/components/CreateHubspotDealModal";
import { CreateZohoDealModal } from "../../../Company/components/CreateZohoDealModal";
import { CreatePipedriveDealModal } from "../../../Company/components/CreatePipedriveDealModal";
import { MessagesAccessMode } from "../../utils/messagesAccessMode";
import { Tabs } from "./outboxTabs";

interface OutboxActionBarProps {
	/** A cancelled company (PREC-3900) loses the send-buttons portal target. */
	accessMode: MessagesAccessMode;
	currentListPerson: ListPerson | undefined;
	currentTab: Tabs;
	/** Portal target the chat tab's send buttons render into. */
	sendButtonsPortalRef: RefObject<HTMLDivElement>;
	/** Whether the user has a custom sequence edit worth saving. */
	showSaveCustom: boolean;
	isSaveCustomDisabled: boolean;
	isSavingCustom: boolean;
	onSaveCustom: () => void;
}

/**
 * The sticky bar at the bottom of the Outbox: save-custom-sequence, pause/resume
 * of the selected lead's outreach, CRM deal creation (HubSpot/Zoho/Pipedrive with
 * their modals), and the portal target for the chat tab's send buttons.
 *
 * Pause/resume are the same decision and the same call as the Lead Panel's — `resolveOutreachHoldState`
 * and `pause-outreach` (PREC-4107). They used to be neither: this bar toggled `active` on
 * `schedules[0]` alone through the legacy `schedule/toggle/:id`, so a lead with three queued steps
 * had one held and two still sending, both buttons then disappeared (their guards were `every(...)`),
 * the lead was filed under the Outbox's Paused tab while still mailing, and the campaign re-armed it
 * on the next pass because nothing wrote `stopCampaignAt`.
 */
export const OutboxActionBar: FunctionComponent<OutboxActionBarProps> = ({
	accessMode,
	currentListPerson,
	currentTab,
	sendButtonsPortalRef,
	showSaveCustom,
	isSaveCustomDisabled,
	isSavingCustom,
	onSaveCustom,
}) => {
	const { t } = useTranslation();
	const { user } = useAppSelector(authSelector);

	const { pauseCampaign, isPausing, resumeCampaign, isResuming, renderSnackbar } =
		useLeadPanelActions({
			prospectId: currentListPerson?.prospect?.id ?? 0,
			prospectName: currentListPerson?.prospect?.name ?? "",
			listPersonId: currentListPerson?.id,
			listPerson: currentListPerson,
		});

	const [showCreateDealModal, setShowCreateDealModal] = useState<boolean>(false);
	const [showCreateZohoDealModal, setShowCreateZohoDealModal] = useState<boolean>(false);
	const [showCreatePipedriveDealModal, setShowCreatePipedriveDealModal] = useState<boolean>(false);

	const isHubspotActive = useIsFeatureActive(FeaturesCodes.HUBSPOT);
	const isZohoActive = useIsFeatureActive(FeaturesCodes.ZOHO_CRM);
	const { data: zohoOAuthStatus } = useGetZohoOAuthStatusQuery(undefined, {
		skip: !isZohoActive,
	});
	const isZohoConnected = zohoOAuthStatus?.connected ?? false;

	const isPipedriveActive = useIsFeatureActive(FeaturesCodes.PIPEDRIVE_CRM);
	const { data: pipedriveOAuthStatus } = useGetPipedriveOAuthStatusQuery(undefined, {
		skip: !isPipedriveActive,
	});
	const isPipedriveConnected = pipedriveOAuthStatus?.connected ?? false;

	const holdState = resolveOutreachHoldState(currentListPerson);

	const dealProspect = currentListPerson?.prospect
		? {
				firstName: currentListPerson.prospect.firstName,
				lastName: currentListPerson.prospect.lastName,
				email: currentListPerson.prospect.email,
				title: currentListPerson.prospect.title,
				organizationName: currentListPerson.prospect.organization?.name,
			}
		: undefined;

	return (
		<>
			<div className="shrink-0 h-12 flex flex-row justify-between items-center border-t border-t-border px-3 py-2 bg-white w-full">
				<div></div>
				<div className="flex flex-row self-left items-center gap-x-2">
					{showSaveCustom && (
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							disabled={isSaveCustomDisabled}
							isLoading={isSavingCustom}
							size={ButtonSize.XS}
							title="Save custom sequence"
							onClick={onSaveCustom}
						/>
					)}
					{currentListPerson && holdState === "resumable" && (
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							disabled={isResuming}
							image={<Icon className="mr-2" icon={faPlay} />}
							size={ButtonSize.XS}
							title={t("contacts.leadPanel.actions.campaign.paused.resume")}
							onClick={() => void resumeCampaign(currentListPerson.id)}
						/>
					)}
					{currentListPerson && holdState === "pausable" && (
						<PauseOutreachButton
							className="shrink-0"
							loading={isPausing}
							testId="outbox-pause-campaign"
							onPause={() => void pauseCampaign(currentListPerson.id)}
						/>
					)}
					{isHubspotActive && user?.company?.hasHubspotApiKey && currentListPerson && (
						<Button
							color={ButtonColor.ACTION}
							image={<Icon className="mr-2" icon={faEuroSign} />}
							size={ButtonSize.XS}
							title={t("company.hubspot.createDeal.title")}
							onClick={() => setShowCreateDealModal(true)}
						/>
					)}
					{isZohoActive && isZohoConnected && currentListPerson && (
						<Button
							color={ButtonColor.ACTION}
							image={<Icon className="mr-2" icon={faEuroSign} />}
							size={ButtonSize.XS}
							title={t("company.zoho.createDeal.title")}
							onClick={() => setShowCreateZohoDealModal(true)}
						/>
					)}
					{isPipedriveActive && isPipedriveConnected && currentListPerson && (
						<Button
							color={ButtonColor.ACTION}
							image={<Icon className="mr-2" icon={faEuroSign} />}
							size={ButtonSize.XS}
							title={t("company.pipedrive.createDeal.title")}
							onClick={() => setShowCreatePipedriveDealModal(true)}
						/>
					)}
					{currentListPerson && currentTab === Tabs.CHAT && accessMode !== "read-only" && (
						<div ref={sendButtonsPortalRef} className="flex items-center gap-x-2" />
					)}
				</div>
			</div>

			{renderSnackbar()}

			{currentListPerson?.prospect?.id && dealProspect && showCreateDealModal && (
				<CreateHubspotDealModal
					isOpen={showCreateDealModal}
					prospect={dealProspect}
					prospectId={currentListPerson.prospect.id}
					onClose={() => setShowCreateDealModal(false)}
				/>
			)}
			{currentListPerson?.prospect?.id && dealProspect && showCreateZohoDealModal && (
				<CreateZohoDealModal
					isOpen={showCreateZohoDealModal}
					prospect={dealProspect}
					prospectId={currentListPerson.prospect.id}
					onClose={() => setShowCreateZohoDealModal(false)}
				/>
			)}
			{currentListPerson?.prospect?.id && dealProspect && showCreatePipedriveDealModal && (
				<CreatePipedriveDealModal
					isOpen={showCreatePipedriveDealModal}
					prospect={dealProspect}
					prospectId={currentListPerson.prospect.id}
					onClose={() => setShowCreatePipedriveDealModal(false)}
				/>
			)}
		</>
	);
};
